import { useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { KeyRound, ArrowLeft, CheckCircle, Mail } from 'lucide-react'

export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [done, setDone] = useState(false)
  const [email, setEmail] = useState('')
  const [linkSent, setLinkSent] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)

  const handleForgotSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const { data } = await api.post('/auth/forgot-password', { email: email.trim() })
      toast.success(data.message || 'Check your email for a reset link.')
      setLinkSent(true)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send reset link')
    } finally {
      setLoading(false)
    }
  }

  const handleResetSubmit = async (e) => {
    e.preventDefault()

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }

    setLoading(true)
    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: newPassword,
      })
      toast.success('Password updated successfully!')
      setDone(true)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reset password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-cinema-black flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/Logos/SMA LOGO  DARK MODE.png"
            alt="Synergy Multimedia Academy"
            className="h-12 mx-auto mb-6 opacity-90"
          />
          <h1 className="font-display text-3xl text-white font-light">
            {token ? 'Set New Password' : 'Reset Password'}
          </h1>
          <p className="text-cinema-muted font-sans text-sm mt-3">
            {done
              ? "You're all set!"
              : token
              ? 'Choose a new password for your account.'
              : linkSent
              ? 'If an account exists with that email, a reset link has been sent.'
              : 'Enter your email and we will send you a secure reset link.'}
          </p>
        </div>

        {done ? (
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <CheckCircle className="w-16 h-16 text-cinema-glow" />
            </div>
            <p className="text-cinema-soft font-sans">
              Your password has been updated. You can now log in.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full cinema-btn-primary justify-center"
            >
              Go to Login
            </button>
          </div>
        ) : token ? (
          <form onSubmit={handleResetSubmit} className="space-y-6">
            <div>
              <label className="cinema-label block mb-3">New Password</label>
              <input
                type="password"
                className="w-full bg-transparent border-0 border-b border-white/10 text-white placeholder:text-cinema-muted focus:border-cinema-gold focus:outline-none transition-colors py-2 px-0 font-sans"
                placeholder="At least 6 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={loading}
                minLength="6"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="cinema-label block mb-3">Confirm New Password</label>
              <input
                type="password"
                className="w-full bg-transparent border-0 border-b border-white/10 text-white placeholder:text-cinema-muted focus:border-cinema-gold focus:outline-none transition-colors py-2 px-0 font-sans"
                placeholder="Repeat your new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                minLength="6"
                autoComplete="new-password"
              />
            </div>
            <button
              type="submit"
              className="w-full cinema-btn-primary justify-center flex items-center gap-2"
              disabled={loading}
            >
              <KeyRound size={18} />
              {loading ? 'Saving…' : 'Update Password'}
            </button>
          </form>
        ) : linkSent ? (
          <div className="space-y-6 text-center">
            <div className="w-16 h-16 bg-cinema-glow/10 rounded-full flex items-center justify-center mx-auto">
              <Mail className="w-8 h-8 text-cinema-glow" />
            </div>
            <p className="text-cinema-soft font-sans text-sm">
              Check your inbox and spam folder. The link expires in 1 hour.
            </p>
            <button
              type="button"
              onClick={() => setLinkSent(false)}
              className="cinema-btn-ghost justify-center w-full"
            >
              Send another link
            </button>
          </div>
        ) : (
          <form onSubmit={handleForgotSubmit} className="space-y-6">
            <div>
              <label className="cinema-label block mb-3">Email Address</label>
              <input
                type="email"
                className="w-full bg-transparent border-0 border-b border-white/10 text-white placeholder:text-cinema-muted focus:border-cinema-gold focus:outline-none transition-colors py-2 px-0 font-sans"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>
            <button
              type="submit"
              className="w-full cinema-btn-primary justify-center flex items-center gap-2"
              disabled={loading}
            >
              <Mail size={18} />
              {loading ? 'Sending…' : 'Send Reset Link'}
            </button>
          </form>
        )}

        <div className="mt-8 text-center">
          <Link
            to="/login"
            className="text-cinema-muted hover:text-cinema-gold font-sans text-sm inline-flex items-center gap-1 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  )
}
